<template>
  <div class="field-list-container my-5 p-4 border rounded shadow-sm bg-light">
    <label>
      <question-display :question="question" customClass="header-fieldlist"></question-display>
    </label>

    <div v-for="(fieldSet, index) in fieldList" :key="fieldSet.id || index" class="field-set mb-4 row">
      <label style="font-size: small; padding-left: 16px; color: gray;">Destination #{{ index + 1 }}</label>

      <!-- key includes currentQ3A4 so Vue destroys/recreates multiselect when residency changes -->
      <template v-for="field in visibleFields" :key="`${currentQ3A4}-${field.key_name}`">
        <div class="form-group mb-3" v-if="field">
        <label v-if="field.display_label">{{ field.field_name }}</label>

        <!-- Textbox -->
        <div class="col-md-12 labelSpacing" v-if="field.type === 'textbox'">
          <input
            type="text"
            :placeholder="field.placeholder"
            v-model="fieldSet[field.key_name]"
            class="form-control mb-3"
            @input="emitFieldListUpdate"
          />
        </div>

        <!-- Textarea -->
        <div class="col-md-12 labelSpacing" v-if="field.type === 'textarea'">
          <textarea 
            :placeholder="field.placeholder"
            v-model="fieldSet[field.key_name]"
            class="form-control mb-3"
            @input="emitFieldListUpdate"
          ></textarea>
        </div>

        <!-- Dropdown (plain select) -->
        <div class="col-md-6 labelSpacing" v-if="field.type === 'dropdown'">
          <select 
            :placeholder="field.placeholder"
            v-model="fieldSet[field.key_name]" 
            class="form-control form-select"
            @change="emitFieldListUpdate"
          >
            <option value="" disabled>Select an option</option>
            <option 
              v-for="option in dropdownOptions[field.key_name]" 
              :key="option.value" 
              :value="option.value"
            >
              {{ option.name }}
            </option>
          </select>
        </div>

        <!-- COUNTRY multiselect: shown for USA residents (destination abroad) -->
        <div
          class="col-md-6 labelSpacing"
          v-if="field.type === 'dropdown-search' && field.key_name === 'country'"
        >
          <multiselect
            v-model="fieldSet[field.key_name]"
            :options="dropdownOptions[field.key_name] || []"
            :searchable="true"
            :close-on-select="true"
            :clear-on-select="false"
            label="name"
            track-by="value"
            :placeholder="field.placeholder || 'Select Country'"
            @update:modelValue="emitFieldListUpdate"
          />
        </div>

        <!-- STATE multiselect: shown for non-USA residents (US state visited) -->
        <div
          class="col-md-6 labelSpacing"
          v-if="field.type === 'dropdown-search' && field.key_name === 'state'"
        >
          <multiselect
            v-model="fieldSet[field.key_name]"
            :options="dropdownOptions[field.key_name] || []"
            :searchable="true"
            :close-on-select="true"
            :clear-on-select="false"
            label="name"
            track-by="value"
            :placeholder="field.placeholder || 'Select State'"
            @update:modelValue="emitFieldListUpdate"
          />
        </div>

        <!-- Number input -->
        <div class="col-md-6 labelSpacing" v-if="field.type === 'number'">
          <input
            type="number"
            autocomplete="off"
            v-model="fieldSet[field.key_name]"
            class="form-control"
            :placeholder="field.placeholder"
            @input="updateInput($event, field, fieldSet)" 
          />
        </div>

        <!-- Radio input -->
        <div v-if="field.type === 'radio'" class="form-check col-sm-12 labelSpacing">
          <label class="mr-2" style="margin-right: 1rem;">{{ field.header }}</label>
          <label class="form-label" v-for="option in field.options" :key="option">
            <input 
              type="radio" 
              :value="option" 
              v-model="fieldSet[field.key_name]" 
              class="form-check-input"
            />
            {{ option }}&nbsp;&nbsp;&nbsp;&nbsp;
          </label>
        </div>
        </div>
      </template>
    </div>

    <!-- Action Buttons -->
    <div class="form-row justify-content-center mt-4">
      <div class="col-12 col-md-5 mb-3">
        <button 
          type="button"
          class="btn btn-danger btn-block btn-add"
          :disabled="fieldList.length <= 1"
          @click="removeFieldSet(fieldList.length - 1)"
        >
          <i class="fa fa-trash"></i> Remove City/Attraction
        </button>
      </div>

      <div class="col-12 col-md-5 mb-3">
        <button 
          type="button"
          class="btn btn-primary btn-block btn-add"
          :disabled="fieldList.length >= 7"
          @click="addFieldSet"
        >
          <i class="fa fa-plus"></i> Add City/Attraction
        </button>
      </div>

      <div class="col-12 text-center">
        <small v-if="fieldList.length >= 7" class="text-danger">
          Max number of destinations reached
        </small>
      </div>
    </div>

    <div class="col-sm-12 text-center mt-2">
      <small v-if="showValidation && required && !fieldList.length" class="text-danger">
        This field is required.
      </small>
    </div>
  </div>
</template>

<script>
import dropdownValues from "@/assets/data/dropdown-values/dropdown.json";
import surveyService from '@/services/surveyService';
import QuestionDisplay from '../utils/QuestionDisplay.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
  components: {
    QuestionDisplay,
    Multiselect
  },
  props: {
    question: Object,
    required: Boolean,
    showValidation: Boolean
  },
  data() {
    return {
      fieldList: (this.question?.id ? surveyService.getAnswer(this.question.id) : null) || [],
      dropdownOptions: {}
    };
  },
  computed: {
    currentQ3A4() {
      // Reactive — Vue tracks this and recomputes visibleFields when Q3A4 changes
      return surveyService.state.answers['3a4'] || '';
    },
    visibleFields() {
      const q3a4 = String(surveyService.state.answers['3a4'] || '').trim();
      return (this.question.fields || []).filter(field => {
        if (!field.condition) return true;
        if (field.condition.question_id === '3a4') {
          const conditionValue = String(field.condition.value || '').trim();
          const isMatch = q3a4 === conditionValue;
          return field.condition.reverse ? !isMatch : isMatch;
        }
        return this.checkFieldCondition(field);
      });
    }
  },
  methods: {
    checkFieldCondition(field) {
      if (!field.condition) return true;

      if (field.condition.question_id === '3a4') {
        const q3a4 = String(surveyService.state.answers['3a4'] || '').trim();
        const conditionValue = String(field.condition.value || '').trim();
        const isMatch = q3a4 === conditionValue;
        return field.condition.reverse ? !isMatch : isMatch;
      }

      const conditionAnswer = surveyService.state.answers[field.condition.question_id];
      let answerValue = conditionAnswer;
      if (conditionAnswer && typeof conditionAnswer === 'object') {
        answerValue = conditionAnswer.key || conditionAnswer.value || conditionAnswer;
      }
      const isMatch = answerValue?.toString() === field.condition.value?.toString();
      return field.condition.reverse ? !isMatch : isMatch;
    },

    updateInput(event, field, fieldSet) {
      const value = Number(event.target.value);
      fieldSet[field.key_name] = value >= 0 && !isNaN(value) ? value : '';
      this.emitFieldListUpdate();
    },

    emitFieldListUpdate() {
      // Clean up hidden fields and enforce Q11 mutual exclusion before saving
      this.fieldList.forEach(fieldSet => {
        this.question.fields?.forEach(field => {
          if (field?.key_name && field.condition && !this.checkFieldCondition(field)) {
            delete fieldSet[field.key_name];
          }
        });

        // Q11: enforce state vs country mutual exclusion based on residency
        if (this.question?.id === '11') {
          const q3a4 = surveyService.state.answers['3a4'] || '';
          const isUSResident = String(q3a4).trim() === '999';

          if (isUSResident) {
            // USA resident → destination is abroad → keep country, remove state
            delete fieldSet.state;
          } else {
            // Non-USA resident → destination is in USA → keep state, remove country
            delete fieldSet.country;
          }
        }
      });

      surveyService.setAnswer(this.question.id, this.fieldList);
      this.$emit("input", this.fieldList);
    },

    addFieldSet() {
      if (this.fieldList.length >= 7) return;
      if (!this.question?.fields) return;

      const newFieldSet = { id: crypto.randomUUID?.() || Date.now() };
      this.question.fields.forEach(field => {
        if (field?.key_name) {
          if (field.condition && !this.checkFieldCondition(field)) return;
          newFieldSet[field.key_name] = "";
        }
      });
      this.fieldList.push(newFieldSet);
      this.emitFieldListUpdate();
    },

    removeFieldSet(index) {
      this.fieldList.splice(index, 1);
      this.emitFieldListUpdate();
    },

    loadDropdownValues() {
      this.dropdownOptions = {};
      this.question.fields?.forEach(field => {
        if ((field.type === 'dropdown' || field.type === 'dropdown-search') && field['selection-list']) {
          const key = field['selection-list'].value;
          const options = dropdownValues[key];
          this.dropdownOptions[field.key_name] = Array.isArray(options) ? options : [];
        }
      });
    },

    initializeFieldList() {
      if (!this.fieldList || !this.fieldList.length) {
        this.addFieldSet();
      }
    },

    cleanupHiddenFields() {
      let hasChanges = false;
      this.fieldList.forEach(fieldSet => {
        this.question.fields?.forEach(field => {
          if (field?.key_name && field.condition && !this.checkFieldCondition(field)) {
            if (fieldSet[field.key_name] !== undefined) {
              delete fieldSet[field.key_name];
              hasChanges = true;
            }
          }
        });

        // Enforce Q11 mutual exclusion on cleanup too
        if (this.question?.id === '11') {
          const q3a4 = surveyService.state.answers['3a4'] || '';
          const isUSResident = String(q3a4).trim() === '999';
          if (isUSResident && fieldSet.state !== undefined) {
            delete fieldSet.state;
            hasChanges = true;
          }
          if (!isUSResident && fieldSet.country !== undefined) {
            delete fieldSet.country;
            hasChanges = true;
          }
        }
      });

      if (hasChanges) {
        this.emitFieldListUpdate();
      }
    }
  },
  watch: {
    question: {
      handler(newQuestion, oldQuestion) {
        if (newQuestion?.id && newQuestion?.id !== oldQuestion?.id) {
          this.fieldList = surveyService.getAnswer(newQuestion.id) || [];
          this.dropdownOptions = {};
          this.loadDropdownValues();
          this.initializeFieldList();
        }
      },
      deep: true,
      immediate: false
    }
  },
  created() {
    // Watch Q3A4 changes to clean up stale state/country values
    this.$watch(
      () => surveyService.state.answers['3a4'],
      (newVal, oldVal) => {
        if (newVal !== oldVal && this.question?.id === '11') {
          this.cleanupHiddenFields();
        }
      },
      { immediate: false }
    );

    // Also watch Q3A in case Q3A4 is derived from it
    this.$watch(
      () => surveyService.state.answers['3a'],
      (newVal, oldVal) => {
        if (newVal !== oldVal && this.question?.id === '11') {
          this.cleanupHiddenFields();
        }
      },
      { immediate: false }
    );
  },
  mounted() {
    this.loadDropdownValues();
    this.initializeFieldList();
  }
};
</script>

<style scoped>
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}

.btn-add {
  margin-bottom: 1rem;
}

.labelSpacing {
  margin-bottom: 1rem;
}
</style>
